import { Injectable, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class CourseIconMappingService {
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly iconMappings: Map<string, string> = new Map([
    // Cyber Security courses
    ['CyberSecurity', 'cyber-security'],
    ['FitForCyberSecurityBasic', 'cyber-security'],
    ['FitForCyberSecurity2', 'cyber-security'],
    ['FitForCyberSecurityRefresher', 'cyber-security'],
    ['CyberSecurityNIS2', 'nis2'],
    ['CyberSecurityEUAIAct', 'using-ai-securely'],
    ['CyberSecurityDORA', 'dora'],
    ['CyberSecurityPrivileged', 'privileged-accounts'],
    ['Deepfake', 'deepfake'],

    // Compliance courses
    ['Compliance', 'compliance'],
    ['FitForComplianceRefresher', 'compliance'],
    ['FitForComplianceTestOut', 'compliance'],
    ['ComplianceCareAlongTheSupplyChain', 'supply-chain'],
    ['ComplianceWhistleblowing', 'whistleblowing'],
    ['ComplianceEqualTreatment', 'equal-treatment'],
    ['ComplianceMoneyLaundering', 'money-laundry'],

    // Data Protection courses
    ['DataProtection', 'data-protection'],
    ['FitForDataProtectionRefresher', 'data-protection'],
    ['FitForDataProtectionTestOut', 'data-protection'],
    ['DataProtectionAndConfidentiality', 'confidentiality'],
    ['DataProtectionWorkingFromHome', 'home-office'],

    // Occupational Safety courses
    ['OccupationalSafety', 'occupational-safety'],
    ['FitForOccupationalSafetyRefresher', 'occupational-safety'],
    ['FitForOccupationalSafetyTestOut', 'occupational-safety'],
    ['OccupationalSafetyInTheOffice', 'occupational-safety'],
    ['OccupationalSafetyInSwitzerland', 'occupational-safety'],
    ['OccupationalSafetyCarPool', 'car-pool'],
  ]);

  private readonly topicAreaFallbacks: Map<string, string> = new Map([
    ['CyberSecurity', 'cyber-security'],
    ['Compliance', 'compliance'],
    ['DataProtection', 'data-protection'],
    ['OccupationalSafety', 'occupational-safety'],
  ]);

  private readonly availableIcons = [
    'car-pool',
    'compliance',
    'confidentiality',
    'cyber-security',
    'data-protection',
    'dora',
    'equal-treatment',
    'home-office',
    'money-laundry',
    'nis2',
    'occupational-safety',
    'privileged-accounts',
    'supply-chain',
    'using-ai-securely',
    'whistleblowing',
    'deepfake',
  ];

  private readonly registeredIcons: Set<string> = new Set();

  constructor() {
    this.preRegisterAllIcons();
  }

  getCourseIcon(courseName: string, topicAreaName?: string): string {
    // First try exact mapping
    let iconName = this.iconMappings.get(courseName);

    // Topic area fallback
    iconName ??= topicAreaName
      ? this.topicAreaFallbacks.get(topicAreaName)
      : undefined;

    // Default fallback
    iconName ??= 'cyber-security';

    return iconName;
  }

  private registerIcon(iconName: string): void {
    if (
      !this.registeredIcons.has(iconName) &&
      this.availableIcons.includes(iconName)
    ) {
      this.iconRegistry.addSvgIcon(
        iconName,
        this.sanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${iconName}.svg`,
        ),
      );
      this.registeredIcons.add(iconName);
    }
  }

  preRegisterAllIcons(): void {
    this.availableIcons.forEach((iconName) => {
      this.registerIcon(iconName);
    });
  }
}
